/**
 * Runtime environment variables injected at container startup.
 * This interface matches the shape of window.__ENV__ generated by docker-entrypoint.sh
 */
interface RuntimeEnv {
  readonly API_URL: string;
  readonly MIXPANEL_PROXY_URL?: string;
  readonly CADACTIVE_DEPLOYMENT?: string;
  readonly SUPPORT_URL?: string;
}

declare global {
  interface Window {
    /**
     * Runtime environment configuration.
     * - In production (Docker): Injected by docker-entrypoint.sh at container startup
     * - In development: Empty/undefined (falls back to import.meta.env)
     */
    __ENV__?: RuntimeEnv;
  }
}

/**
 * Get the API URL from environment variables.
 * 
 * Priority order:
 * 1. Runtime env (window.__ENV__.API_URL) - Set at container startup for production/staging
 * 2. Build-time env (import.meta.env.API_URL) - Set during build or in .env files for development
 * 3. Fallback default - Development API server
 * 
 * Environment variable naming:
 * - Use plain `API_URL` everywhere (Docker, Cloud Run, .env files)
 * - Vite exposes it via define config in vite.config.mjs
 * - No VITE_ prefix needed - keeping naming consistent with backend/devops conventions
 * 
 * @returns The API URL for browser use (NOT for Vite proxy)
 */
export function getApiUrl(): string {
  const apiUrl = 
    window.__ENV__?.API_URL ||
    import.meta.env.API_URL || 
    'https://api-dev.cadactive.com:443';
  
  // For browser redirects, always use 'localhost' not 'host.docker.internal'
  // The browser runs on the host machine and needs to reach the host's localhost
  // The Vite proxy (in vite.config.mjs) will handle the container-to-host translation
  return apiUrl.replace('host.docker.internal', 'localhost');
}

/**
 * Get the full URL for an API endpoint.
 * 
 * In development (when running via Vite dev server):
 * - Uses relative URLs (e.g., '/api/me') which go through Vite proxy to avoid CORS
 * 
 * In production (Docker/Cloud Run):
 * - Uses absolute URLs from API_URL environment variable
 * 
 * @param path - The API endpoint path (e.g., '/api/me', '/api/specs')
 * @returns The full URL or relative path to the API endpoint
 * 
 * @example
 * // Development: getApiEndpoint('/api/me') → '/api/me' (proxied by Vite)
 * // Production: getApiEndpoint('/api/me') → 'https://api.cadactive.com/api/me'
 */
export function getApiEndpoint(path: string): string {
  // In development (Vite dev server), use relative URLs to go through proxy
  // The Vite proxy will forward /api/* and /auth/* to the backend API
  const isDevelopment = import.meta.env.DEV;
  
  if (isDevelopment) {
    // Return relative path for Vite proxy
    return path.startsWith('/') ? path : `/${path}`;
  }
  
  // In production, use absolute URL
  const apiUrl = getApiUrl();
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${apiUrl}/${cleanPath}`;
}

/**
 * Get the full URL for an API auth endpoint.
 * 
 * @param path - The auth path (e.g., '/auth/sign_in', '/auth/sessions/sign_out')
 * @returns The full URL to the API auth endpoint
 * 
 * @example
 * getAuthUrl('/auth/sign_in') → 'http://localhost:3000/auth/sign_in'
 */
export function getAuthUrl(path: string): string {
  return getApiEndpoint(path);
}
